/**
 * Aritmetica delle settimane, in un posto solo.
 *
 * Un giorno qui e sempre una stringa `AAAA-MM-GG`, non un istante: il programma settimanale
 * ragiona per giorni di calendario, e un istante porta con se un fuso che a mezzanotte
 * sposta il giorno. L'istante vero lo calcola `zonedWallClockToUtc` al momento di creare
 * l'intervento, che e l'unico punto in cui serve.
 *
 * La settimana comincia di lunedi, come la scrive il cliente sul suo quaderno.
 */
export const WEEK_DAYS = 7;

/** Giorni della settimana in numeri ISO: 1 lunedi, 7 domenica. */
export const WEEKDAY_LABELS: Record<number, string> = {
  1: "Lunedi",
  2: "Martedi",
  3: "Mercoledi",
  4: "Giovedi",
  5: "Venerdi",
  6: "Sabato",
  7: "Domenica",
};

/**
 * Ogni quante settimane, al massimo, puo tornare un giro fisso.
 *
 * Il giro vero del cliente non e solo settimanale o quindicinale: la Rampa Isola Blu e il
 * giardino di Anna tornano ogni quattro settimane. Otto settimane sono il tetto: oltre, il
 * lavoro non e piu un giro fisso ma un appuntamento da programmare a mano.
 */
export const MAX_INTERVAL_WEEKS = 8;

const NUMERI_A_PAROLE: Record<number, string> = {
  2: "due",
  3: "tre",
  4: "quattro",
  5: "cinque",
  6: "sei",
  7: "sette",
  8: "otto",
};

/** Il ritmo detto come lo direbbe una persona: "ogni settimana", "ogni quattro settimane". */
export function intervalWeeksLabel(intervalWeeks: number): string {
  if (intervalWeeks <= 1) return "ogni settimana";
  return `ogni ${NUMERI_A_PAROLE[intervalWeeks] ?? intervalWeeks} settimane`;
}

const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isDateKey(value: string): boolean {
  const match = DATE_KEY_PATTERN.exec(value);
  if (!match) return false;
  const [, year, month, day] = match;
  const instant = Date.UTC(Number(year), Number(month) - 1, Number(day));
  const date = new Date(instant);
  return (
    date.getUTCFullYear() === Number(year) &&
    date.getUTCMonth() === Number(month) - 1 &&
    date.getUTCDate() === Number(day)
  );
}

function assertDateKey(value: string): void {
  if (!isDateKey(value)) throw new Error(`Giorno non valido: ${value}`);
}

function toUtcMidnight(dateKey: string): number {
  assertDateKey(dateKey);
  const [year, month, day] = dateKey.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
}

function fromUtcMidnight(instant: number): string {
  const date = new Date(instant);
  const year = String(date.getUTCFullYear()).padStart(4, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addDays(dateKey: string, days: number): string {
  return fromUtcMidnight(toUtcMidnight(dateKey) + days * 86_400_000);
}

/** Giorni fra due date, positivo se la seconda viene dopo la prima. */
export function daysBetween(fromDateKey: string, toDateKey: string): number {
  return Math.round((toUtcMidnight(toDateKey) - toUtcMidnight(fromDateKey)) / 86_400_000);
}

/** Numero ISO del giorno: 1 lunedi ... 7 domenica. */
export function weekdayOf(dateKey: string): number {
  const day = new Date(toUtcMidnight(dateKey)).getUTCDay();
  return day === 0 ? 7 : day;
}

/** Il lunedi della settimana che contiene questo giorno. */
export function weekStartOf(dateKey: string): string {
  return addDays(dateKey, 1 - weekdayOf(dateKey));
}

/** I sette giorni della settimana che comincia al lunedi indicato. */
export function weekDayKeys(weekStartDateKey: string): string[] {
  const start = weekStartOf(weekStartDateKey);
  return Array.from({ length: WEEK_DAYS }, (_, index) => addDays(start, index));
}

/**
 * Il giorno di una serie che cade dentro questa settimana, se ce n'e uno.
 *
 * Le ricorrenze si contano dal primo giorno del programma: ogni `intervalWeeks` settimane
 * esatte. Cosi un giro quindicinale resta agganciato alla settimana in cui e nato e non
 * scivola quando qualcuno guarda una settimana lontana.
 */
export function occurrenceInWeek(
  series: { weekday: number; startDate: string; endDate?: string | null; intervalWeeks: number },
  weekStartDateKey: string,
): string | null {
  const weekStart = weekStartOf(weekStartDateKey);
  const candidate = addDays(weekStart, series.weekday - 1);
  if (daysBetween(series.startDate, candidate) < 0) return null;
  if (series.endDate && daysBetween(series.endDate, candidate) > 0) return null;
  const seriesWeekStart = weekStartOf(series.startDate);
  const weeksApart = daysBetween(seriesWeekStart, weekStart) / 7;
  if (!Number.isInteger(weeksApart) || weeksApart < 0) return null;
  return weeksApart % series.intervalWeeks === 0 ? candidate : null;
}

/** Tutte le occorrenze di una serie fra due giorni, estremi compresi. */
export function occurrencesBetween(
  series: { weekday: number; startDate: string; endDate?: string | null; intervalWeeks: number },
  fromDateKey: string,
  toDateKey: string,
): string[] {
  const occurrences: string[] = [];
  let weekStart = weekStartOf(fromDateKey);
  const last = weekStartOf(toDateKey);
  while (daysBetween(weekStart, last) >= 0) {
    const occurrence = occurrenceInWeek(series, weekStart);
    if (occurrence && daysBetween(fromDateKey, occurrence) >= 0 && daysBetween(occurrence, toDateKey) >= 0) {
      occurrences.push(occurrence);
    }
    weekStart = addDays(weekStart, 7);
  }
  return occurrences;
}

/** Ora del giorno leggibile a partire dai minuti dalla mezzanotte. */
export function formatDayMinute(minutes: number): string {
  const total = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** Minuti dalla mezzanotte a partire da un orario `HH:MM`. */
export function parseDayMinute(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}
